import { useEffect, useState } from "react";
import { AlertTriangle, Copy, Save, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, BACKEND_URL } from "../../../lib/api";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

// Shown until Meta is actually delivering webhooks to GDIOT PAY (no webhook = no replies in the inbox).
export function InboxSetupBanner() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/whatsapp/webhook-config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!cfg) return null;
  const receiving = cfg.app_secret_set && cfg.events_received > 0;
  if (receiving) return null;
  const callback = `${BACKEND_URL}/api/whatsapp/webhook`;
  const problem = !cfg.app_secret_set ? "App Secret is not saved, so Meta's messages can't be accepted."
    : cfg.events_unverified > 0 ? "Meta is sending messages, but the App Secret doesn't match — re-check and save it."
    : "Meta hasn't delivered any message to GDIOT PAY yet — the Callback URL in Meta isn't pointing here.";
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/whatsapp/webhook-config", { app_secret: secret }); setCfg(data); setSecret(""); toast.success("App Secret saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const Row = ({ label, value, testid }) => (
    <div className="flex items-center gap-2 bg-white rounded-lg border border-amber-200 px-2.5 py-1.5">
      <span className="text-[11px] text-slate-500 w-24 flex-shrink-0">{label}</span>
      <code className="flex-1 text-xs break-all text-slate-800" data-testid={testid}>{value}</code>
      <button onClick={() => copy(value)} className="text-amber-700"><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm" data-testid="inbox-setup-banner">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left text-amber-900">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1"><b>Parent replies are not reaching the inbox.</b> {problem}</span>
        <span className="text-xs font-semibold underline flex items-center gap-0.5">How to fix <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
        <div className="mt-3 grid md:grid-cols-2 gap-4 pb-1">
          <ol className="list-decimal pl-5 space-y-1 text-xs text-amber-900">
            <li>Open <b>developers.facebook.com</b> → your WhatsApp app → <b>WhatsApp → Configuration</b>.</li>
            <li>Under <b>Webhook</b> click <b>Edit</b>, paste the Callback URL and Verify token shown here, then <b>Verify and save</b>.</li>
            <li>In <b>Webhook fields</b>, turn on <b>messages</b> (Subscribe).</li>
            <li>Open <b>App settings → Basic</b>, click <b>Show</b> next to App Secret, and paste it on the right.</li>
            <li>Your Cloudflare worker keeps working — GDIOT PAY forwards an exact copy of every message to it{cfg.forward_url ? "" : " (no forward URL set)"}.</li>
            <li>Send "Hi" from any phone to your business number; it appears here within seconds.</li>
          </ol>
          <div className="space-y-2">
            <Row label="Callback URL" value={callback} testid="inbox-setup-callback" />
            <Row label="Verify token" value={cfg.verify_token} testid="inbox-setup-verify-token" />
            <div className="flex gap-2">
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={cfg.app_secret_set ? "App Secret saved ✓ (paste to replace)" : "Paste Meta App Secret"} className="flex-1 rounded-lg border border-amber-200 px-2.5 py-1.5 text-sm" data-testid="inbox-setup-secret-input" />
              <button onClick={save} disabled={!secret.trim() || busy} className="px-3 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm flex items-center gap-1" data-testid="inbox-setup-secret-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
            </div>
            <div className="text-[11px] text-amber-800">Received so far: {cfg.events_received} · rejected: {cfg.events_unverified}{cfg.last_event_at ? ` · last ${new Date(cfg.last_event_at).toLocaleString("en-IN")}` : ""}</div>
          </div>
        </div>
      )}
    </div>
  );
}
